export type MetricTone = 'value' | 'unit' | 'muted' | 'prefix'

export type MetricPart = {
	text: string
	tone: MetricTone
}

export type MetricBadge = {
	text: string
	tone: 'neutral' | 'alert' | 'percent'
}

export type MetricCardData = {
	id: string
	parts: MetricPart[]
	label: string
	labelNote?: string
	badge?: MetricBadge
	aside?: string
	empty?: boolean
}

export type MetricRowData = {
	columns: 3 | 4
	cards: MetricCardData[]
}

export type MetricSectionData = {
	id: string
	title: string
	rows: MetricRowData[]
}

const nb = (value: string) => value.replace(/ /g, '\u00A0')

const value = (text: string): MetricPart => ({ text: nb(text), tone: 'value' })
const unit = (text: string): MetricPart => ({ text, tone: 'unit' })
const muted = (text: string): MetricPart => ({ text: nb(text), tone: 'muted' })
const prefix = (text: string): MetricPart => ({ text, tone: 'prefix' })

const groupBadge = (text: string): MetricBadge => ({
	text,
	tone: 'neutral',
})

const alertBadge = (text: string): MetricBadge => ({
	text,
	tone: 'alert',
})

/**
 * Цифры и подписи базового вида «За день» с макета.
 * Формулы и зависимость от фильтров подключим отдельно по блокам.
 */
export const DAY_METRIC_SECTIONS: MetricSectionData[] = [
	{
		id: 'cleanups',
		title: 'Уборки',
		rows: [
			{
				columns: 4,
				cards: [
					{
						id: 'total-remaining',
						parts: [value('2 456'), unit('кг')],
						label: 'Остаток всего',
					},
					{
						id: 'min-remaining',
						parts: [value('4'), unit('%')],
						label: 'Минимальный остаток',
						badge: groupBadge('Группа 9'),
					},
					{
						id: 'max-remaining',
						parts: [value('4'), unit('%')],
						label: 'Максимальный остаток',
						badge: groupBadge('Группа 4'),
					},
					{
						id: 'avg-remaining',
						parts: [value('4'), unit('%')],
						label: 'Средний остаток',
					},
				],
			},
			{
				columns: 3,
				cards: [
					{
						id: 'cleanup-time',
						parts: [value('80'), unit('%')],
						label: 'Соблюдение времени уборки',
						badge: alertBadge('20% опозданий'),
					},
					{
						id: 'cleanup-deviation',
						parts: [value('54'), unit('мин')],
						label: 'Максимальное отклонение от времени уборки',
						badge: groupBadge('Группа 14'),
					},
					{
						id: 'missed-cleanups',
						parts: [value('12'), unit('уборок')],
						label: 'Пропущено уборок',
						badge: alertBadge('10%'),
					},
				],
			},
		],
	},
	{
		id: 'distributions',
		title: 'Раздачи',
		rows: [
			{
				columns: 3,
				cards: [
					{
						id: 'distributed-total',
						parts: [value('34 534'), unit('кг'), muted('/ 32 566 кг')],
						label: 'Роздано корма всего',
						labelNote: '(факт/план)',
					},
					{
						id: 'weight-deviation',
						parts: [value('+9 678'), unit('кг')],
						label: 'Максимальное отклонение от планового веса раздачи',
						badge: groupBadge('Группа 1'),
					},
					{
						id: 'distributed-avg',
						parts: [value('4 534'), unit('кг'), muted('/ 3 566 кг')],
						label: 'Средний вес раздан за день',
						labelNote: '(факт/план)',
					},
				],
			},
			{
				columns: 3,
				cards: [
					{
						id: 'distribution-time',
						parts: [value('80'), unit('%')],
						label: 'Соблюдение времени раздачи',
						badge: alertBadge('20% опозданий'),
					},
					{
						id: 'distribution-deviation',
						parts: [value('9'), unit('ч'), value('12'), unit('мин')],
						label: 'Максимальное отклонение от времени раздачи',
						badge: groupBadge('Группа 14'),
					},
					{
						id: 'empty-table',
						parts: [value('34'), unit('мин')],
						label: 'Кормовой стол пустой',
					},
				],
			},
		],
	},
	{
		id: 'nudges',
		title: 'Подталкивания',
		rows: [
			{
				columns: 4,
				cards: [
					{
						id: 'nudge-schedule',
						parts: [value('54'), unit('%')],
						label: 'Выполнено подталкиваний по расписанию',
						badge: alertBadge('20% качественно'),
					},
					{
						id: 'nudge-min',
						parts: [value('0'), unit('раз/день')],
						label: 'Минимальное количество подталкиваний',
						badge: groupBadge('Группа 9'),
					},
					{
						id: 'nudge-max',
						parts: [value('12'), unit('раз/день')],
						label: 'Максимальное количество подталкиваний',
						badge: groupBadge('Группа 9'),
					},
					{
						id: 'nudge-avg',
						parts: [value('80'), unit('раз/день')],
						label: 'Среднее количество подталкиваний',
					},
				],
			},
			{
				columns: 3,
				cards: [
					{
						id: 'nudge-drop',
						parts: [prefix('на'), value('54'), unit('%')],
						label: 'Средний спад недоступного корма после подталкивания',
					},
					{
						id: 'nudge-day',
						parts: [value('56'), unit('раз/день')],
						label: 'Среднее количество подталкиваний за день',
						aside: 'с 06:00-21:00',
					},
					{
						id: 'nudge-night',
						parts: [value('24'), unit('раз/день')],
						label: 'Среднее количество подталкиваний за ночь',
						aside: 'с 21:00-06:00',
					},
				],
			},
		],
	},
	{
		id: 'psv',
		title: 'Потребление сухого вещества',
		rows: [
			{
				columns: 4,
				cards: [
					{
						id: 'psv-avg',
						parts: [value('34'), unit('кг/гол'), muted('/ 32 кг/гол')],
						label: 'Среднее ПСВ',
						labelNote: '(факт/план)',
					},
				],
			},
		],
	},
]
